import type { ProjectConfig } from "../projects/types";

/** Shape of react-i18next's t() we rely on (avoids version-coupled TFunction generics). */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Resolved landing copy consumed by DisplayHasAccount. */
export interface ResolvedWelcomeCopy {
  badge?: string;
  title: string;
  tagline: string;
  note?: string;
  /** Sign-up button label; undefined = the shared t("home_has_account_false"). */
  registerLabel?: string;
}

/** Replace every {{appName}} (with optional surrounding whitespace) in project copy.
 *  The shared t() fallback strings interpolate {{appName}} themselves via i18n
 *  defaultVariables, so this is only applied to project-authored strings. */
const interpolateAppName = (value: string, appName?: string): string =>
  value.replace(/\{\{\s*appName\s*\}\}/g, appName ?? "");

/**
 * Resolve per-project, per-language welcome copy for the landing screen
 * (DisplayHasAccount).
 *
 * Language resolution order: exact language ("fr-CA") -> base language ("fr") ->
 * a "default" entry -> the first authored entry. When the project declares NO
 * welcome copy at all, falls back to the shared t("home_welcome") /
 * t("home_description") strings (which already carry {{appName}} via i18n
 * defaultVariables), so every current deployment is byte-for-byte unchanged.
 *
 * @param project  active ProjectConfig (from getProjectConfig)
 * @param language i18n.resolvedLanguage ?? i18n.language
 * @param t        for the shared-key fallback
 * @param appName  app?.name from useConfig() — interpolated into {{appName}}
 */
export function resolveWelcomeCopy(
  project: ProjectConfig,
  language: string,
  t: Translate,
  appName?: string
): ResolvedWelcomeCopy {
  const copyByLang = project.welcome;
  const baseLang = language?.split("-")[0];

  const entry =
    copyByLang?.[language] ??
    (baseLang ? copyByLang?.[baseLang] : undefined) ??
    copyByLang?.default ??
    (copyByLang ? Object.values(copyByLang)[0] : undefined);

  if (entry) {
    return {
      badge: entry.badge ? interpolateAppName(entry.badge, appName) : undefined,
      title: interpolateAppName(entry.title, appName),
      tagline: interpolateAppName(entry.tagline, appName),
      note: entry.note ? interpolateAppName(entry.note, appName) : undefined,
      registerLabel: entry.registerLabel
        ? interpolateAppName(entry.registerLabel, appName)
        : undefined,
    };
  }

  // No project copy → shared strings (unchanged behavior for existing studies).
  return {
    title: t("home_welcome"),
    tagline: t("home_description"),
  };
}
